/**
 * Programa de recomendação (rota /referral) — UI enxuta de 17/10/2026, sobre a proposta do
 * Claude Design: um cartão com o saldo (disponível, a receber, quanto falta para o próximo
 * período) e o link; o extrato de ganhos e resgates em lista; "Como funciona e regulamento"
 * recolhido. O resgate soma dias direto na licença (RedeemDialog).
 * Números do GET /app/ambassador; percentuais e prazo vêm do backend, por pessoa.
 *
 * Movimento (23/10/2026): na primeira vez na sessão, o saldo (disponível e a receber) conta de 0
 * até o número real (contarAte, 700 ms); depois, os números já aparecem no lugar. A barra mostra
 * a fração real desde a primeira pintura, sem animar: progresso animado é para etapa que termina,
 * não para saldo (catálogo, "Progresso com número"). Resgate e regulamento abrem e fecham com
 * usePresenca (.ds-veu, .ds-dialogo, .is-aberto).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { contarAte } from '../../lib/movimento';
import { useSessionStore } from '../../stores/sessionStore';
import {
  useReferralSummary,
  type ReferralEarning,
  type ReferralRedemption,
  type ReferralSummary,
} from '../../api/useReferral';
import { RichText } from '../../components/ui/RichText';
import { CardPageSkeleton } from '../../components/common/Skeleton';
import { LinkCard } from './LinkCard';
import { RedeemDialog } from './RedeemDialog';
import { largestStop, nextStop, planEarning, planKey } from './referralMath';
import { useReferralText, type ReferralKey } from './referralText';
import './referral.css';

/** Percentual padrão (config/ambassador.json) enquanto os números não chegam. */
const DEFAULT_PERCENT = 10;
const DEFAULT_HOLD = 10;
/** Linhas do extrato antes de "Ver extrato completo". */
const STATEMENT_PREVIEW = 3;
/** Marca no lugar dos dias em "+{{days}} em {{date}}", para só o número contar. */
const MARCA = '\u2063';
/** O saldo conta uma vez por sessão; ao voltar à tela, o número já está lá. */
let saldoContado = false;

function Icon({ name }: { name: string }): JSX.Element {
  return (
    <span className="material-symbols-rounded" aria-hidden="true">
      {name}
    </span>
  );
}

function Html({ html, className }: { html: string; className?: string }): JSX.Element {
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function ReferralDashboard(): JSX.Element | null {
  const session = useSessionStore((s) => s.session);
  const brand = useBrand();
  const query = useReferralSummary(Boolean(session?.ambassador?.slug));
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const redeem = usePresenca(redeemOpen);
  const rules = usePresenca(rulesOpen);
  const { tx } = useReferralText();

  // Fundo cinza claro da página inteira, como na Home 3B.
  useEffect(() => {
    document.documentElement.classList.add('rf-page');
    return () => document.documentElement.classList.remove('rf-page');
  }, []);

  const slug = session?.ambassador?.slug;
  if (!slug) return null;
  if (query.isPending) return <CardPageSkeleton />;

  const summary = query.data;
  const product = brand.appTitle;
  const discount = summary?.discount ?? session?.ambassador?.discount ?? DEFAULT_PERCENT;
  const percent = summary?.earnPercent ?? DEFAULT_PERCENT;
  const hold = summary?.holdDays ?? DEFAULT_HOLD;

  return (
    <div className="rf">
      <div className="rf-head">
        <span className="rf-eyebrow">
          <Icon name="redeem" />
          {tx('eyebrow')}
        </span>
        <h1>{tx('title', { product })}</h1>
        <p className="rf-lead">
          <Html html={tx('lead', { discount, percent })} />
        </p>
      </div>

      <section className="rf-card rf-main">
        <div className="rf-main__balance">
          {summary ? (
            <Balance summary={summary} percent={percent} onRedeem={() => setRedeemOpen(true)} />
          ) : (
            <LoadError onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </div>
        <div className="rf-main__link">
          <LinkCard
            slug={slug}
            source={brand.source}
            product={product}
            discount={discount}
            stats={
              summary
                ? { access: summary.stats.access, purchases: summary.stats.purchases }
                : undefined
            }
          />
        </div>
      </section>

      {summary ? <Statements summary={summary} /> : null}

      <HowAndRules
        discount={discount}
        percent={percent}
        hold={hold}
        product={product}
        onOpenRules={() => setRulesOpen(true)}
      />

      {redeem.montado && summary ? (
        <RedeemDialog
          summary={summary}
          product={product}
          aberto={redeem.aberto}
          onClose={() => setRedeemOpen(false)}
        />
      ) : null}
      {rules.montado ? (
        <RulesDialog aberto={rules.aberto} onClose={() => setRulesOpen(false)} />
      ) : null}
    </div>
  );
}

/* ---------- Saldo ---------- */

function Balance({
  summary,
  percent,
  onRedeem,
}: {
  summary: ReferralSummary;
  percent: number;
  onRedeem: () => void;
}): JSX.Element {
  const { tx, days, number, date, period } = useReferralText();
  const available = summary.balance.availableDays;
  const pending = summary.balance.pendingDays;
  const nextAt = summary.balance.nextAvailableAt;
  const lifetime = summary.license.lifetime;

  // A barra mede o caminho até o próximo período de resgate, contando o que está a receber.
  const target = nextStop(available + pending) || 365;
  const availablePct = Math.min(100, (available / target) * 100);
  const totalPct = Math.min(100, ((available + pending) / target) * 100);
  const missing = Math.max(0, target - available - pending);
  const yearly = planEarning(365, percent);
  const plans = Math.max(1, Math.ceil(missing / Math.max(1, yearly)));

  let hint: ReactNode;
  if (lifetime) hint = tx('hint_lifetime');
  else if (available + pending === 0) hint = tx('hint_empty', { days: days(yearly) });
  else if (missing === 0) hint = <Html html={tx('ready_max')} />;
  else
    hint = (
      <Html
        html={tx('missing_next', {
          missing: days(missing),
          period: period(target),
          plans: plans === 1 ? tx('plans_one') : tx('plans_other', { n: plans }),
        })}
      />
    );

  const canRedeem = !lifetime && largestStop(available) > 0;

  // Movimento: na primeira vez da sessão, os números contam de 0.
  const [primeira] = useState(() => !saldoContado);
  const availableRef = useRef<HTMLSpanElement | null>(null);
  const pendingRef = useRef<HTMLSpanElement | null>(null);
  const pendingParts = tx('pending_inline', { days: MARCA, date: nextAt ? date(nextAt, true) : '' })
    .split(MARCA);

  // Antes da pintura, para o número real não piscar antes de a contagem começar do zero. O leitor
  // de tela não ouve a contagem (não é região viva); a barra diz os números finais no aria-label.
  useLayoutEffect(() => {
    if (!primeira) return undefined;
    saldoContado = true;
    const contar = (
      el: HTMLSpanElement | null,
      ate: number,
      formato: (n: number) => string,
    ): { stop: () => void } | undefined => {
      if (!el) return undefined;
      el.textContent = formato(0); // sem movimento, contarAte põe o final na hora
      return contarAte(el, 0, ate, formato);
    };
    const contagens = [
      contar(availableRef.current, available, number),
      contar(pendingRef.current, pending, days),
    ];
    return () => contagens.forEach((c) => c?.stop());
    // Só na montagem: depois, o React mostra os números novos no lugar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <span className="rf-label">{tx('available_label')}</span>
      <div className="rf-amount">
        <span ref={availableRef} className="rf-amount__number" data-testid="rf-available">
          {number(available)}
        </span>
        <span className="rf-amount__unit">{tx(available === 1 ? 'unit_one' : 'unit_other')}</span>
        {pending > 0 && nextAt ? (
          <span className="rf-amount__pending" data-testid="rf-pending">
            {pendingParts.length === 2 ? (
              <>
                {pendingParts[0]}
                <span ref={pendingRef} className="rf-amount__count">
                  {days(pending)}
                </span>
                {pendingParts[1]}
              </>
            ) : (
              tx('pending_inline', { days: days(pending), date: date(nextAt, true) })
            )}
          </span>
        ) : null}
      </div>
      <div
        className="rf-progress"
        role="img"
        aria-label={tx('progress_aria', {
          available: days(available),
          pending: days(pending),
          target: period(target),
        })}
      >
        <span className="rf-progress__pending" style={{ width: `${totalPct}%` }} />
        <span className="rf-progress__available" style={{ width: `${availablePct}%` }} />
      </div>
      <p className="rf-hint">{hint}</p>
      <div className="rf-actions">
        <button
          type="button"
          className="rf-btn rf-btn--dark ds-resposta"
          disabled={!canRedeem}
          onClick={onRedeem}
        >
          {tx('redeem_button')}
        </button>
        {!canRedeem && !lifetime ? (
          <span className="rf-actions__note">
            {pending > 0 && nextAt
              ? tx('redeem_locked_on', { date: date(nextAt, true) })
              : tx('redeem_locked')}
          </span>
        ) : null}
      </div>
    </>
  );
}

function LoadError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }): JSX.Element {
  const { tx } = useReferralText();
  return (
    <div className="rf-error" role="alert">
      <h2>{tx('error_title')}</h2>
      <p>{tx('error_text')}</p>
      <button
        type="button"
        className="rf-btn rf-btn--dark ds-resposta"
        onClick={onRetry}
        disabled={retrying}
      >
        <Icon name="refresh" />
        {tx('retry')}
      </button>
    </div>
  );
}

/* ---------- Extrato ---------- */

function planLabel(
  tx: ReturnType<typeof useReferralText>['tx'],
  days: (n: number) => string,
  e: ReferralEarning,
): string {
  const key = planKey(e.timeCycle, e.daysContracted);
  return key === 'other'
    ? tx('plan_other', { days: days(e.daysContracted) })
    : tx(`plan_${key}` as ReferralKey);
}

function Statements({ summary }: { summary: ReferralSummary }): JSX.Element {
  const { tx, days, date } = useReferralText();
  const [tab, setTab] = useState<'earnings' | 'redemptions'>('earnings');
  const [full, setFull] = useState(false);

  const rows: { key: string; title: string; when: string; amount: ReactNode; line?: ReactNode }[] =
    tab === 'earnings'
      ? summary.earnings.map((e) => ({
          key: e.id,
          title: planLabel(tx, days, e),
          when: date(e.paidAt),
          amount: (
            <span
              className={
                e.status === 'cancelled'
                  ? 'rf-row__amount rf-row__amount--cancelled'
                  : 'rf-row__amount'
              }
            >
              +{days(e.days)}
            </span>
          ),
          line:
            e.status === 'pending' ? (
              <span className="rf-row__line rf-row__line--pending">
                {tx('line_pending', { date: date(e.availableAt, true) })}
              </span>
            ) : e.status === 'cancelled' ? (
              <span className="rf-row__line rf-row__line--cancelled">{tx('line_cancelled')}</span>
            ) : (
              <span className="rf-row__line rf-row__line--available">{tx('line_available')}</span>
            ),
        }))
      : summary.redemptions.map((r: ReferralRedemption, i) => ({
          key: `${r.date}-${i}`,
          title:
            r.mode === 'license'
              ? tx('redemption_license')
              : tx('redemption_code', { code: r.code ?? '' }),
          when: date(r.date),
          amount: <span className="rf-row__amount">{days(r.days)}</span>,
        }));
  const visible = full ? rows : rows.slice(0, STATEMENT_PREVIEW);

  return (
    <section className="rf-statements" id="rf-extrato">
      <div className="rf-statements__head">
        <h2>{tx('statements_title')}</h2>
        <div className="rf-tabs" role="group" aria-label={tx('statements_which')}>
          {(['earnings', 'redemptions'] as const).map((id) => (
            <button
              key={id}
              type="button"
              aria-pressed={tab === id}
              onClick={() => {
                setTab(id);
                setFull(false);
              }}
            >
              {tx(id === 'earnings' ? 'tab_earnings' : 'tab_redemptions')}
            </button>
          ))}
        </div>
      </div>

      {rows.length ? (
        <ul className="rf-rows">
          {visible.map((row) => (
            <li key={row.key} className="rf-row">
              <span className="rf-row__left">
                <strong>{row.title}</strong>
                <span>{row.when}</span>
              </span>
              <span className="rf-row__right">
                {row.amount}
                {row.line}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rf-empty">
          {tx(tab === 'earnings' ? 'earnings_empty' : 'redemptions_empty')}
        </p>
      )}

      <div className="rf-statements__foot">
        {rows.length > STATEMENT_PREVIEW ? (
          <button type="button" className="rf-link ds-resposta" onClick={() => setFull((v) => !v)}>
            {full ? tx('see_less') : tx('see_full')}
          </button>
        ) : (
          <span />
        )}
        <span className="rf-privacy">
          <Icon name="lock" />
          {tx('privacy_statement')}
        </span>
      </div>
    </section>
  );
}

/* ---------- Como funciona e regulamento ---------- */

function HowAndRules({
  discount,
  percent,
  hold,
  product,
  onOpenRules,
}: {
  discount: number;
  percent: number;
  hold: number;
  product: string;
  onOpenRules: () => void;
}): JSX.Element {
  const { tx } = useReferralText();
  const steps = [
    { title: tx('how_1_title'), text: tx('how_1_text') },
    { title: tx('how_2_title', { discount }), text: tx('how_2_text') },
    { title: tx('how_3_title', { percent }), text: tx('how_3_text', { hold }) },
    { title: tx('how_4_title'), text: tx('how_4_text', { product }) },
  ];
  const rules = [
    tx('rules_1', { discount }),
    tx('rules_2', { percent }),
    tx('rules_3', { hold }),
    tx('rules_4'),
    tx('rules_5'),
    tx('rules_6'),
  ];
  return (
    <details className="rf-more">
      <summary>
        {tx('how_rules_title')}
        {/* Seta que gira (.ds-seta): 180° em 140 ms; sem movimento, vira na hora. */}
        <span className="material-symbols-rounded ds-seta" aria-hidden="true">
          expand_more
        </span>
      </summary>
      <div className="rf-more__body">
        <ol className="rf-steps">
          {steps.map((step, i) => (
            <li key={step.title}>
              <span className="rf-steps__number">{i + 1}</span>
              <span>
                <strong>{step.title}</strong>
                <span>{step.text}</span>
              </span>
            </li>
          ))}
        </ol>
        <div className="rf-rules">
          <h3>{tx('rules_title')}</h3>
          <ul>
            {rules.map((line) => (
              <li key={line}>
                <Icon name="check" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="rf-link ds-resposta" onClick={onOpenRules}>
            {tx('rules_full')}
          </button>
        </div>
      </div>
    </details>
  );
}

/**
 * Regulamento completo: o mesmo texto aceito na adesão (ambassador_terms). Movimento: quem abre
 * mantém montado com usePresenca e passa `aberto` (.is-aberto em .ds-veu e .ds-dialogo).
 */
export function RulesDialog({
  onClose,
  aberto = true,
}: {
  onClose: () => void;
  aberto?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { tx } = useReferralText();
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className={`rf-overlay ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <section
        className={`rf-dialog ds-dialogo${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rf-rules-title"
      >
        <div className="rf-dialog__head">
          <div>
            <h2 id="rf-rules-title">{tx('rules_full_title')}</h2>
          </div>
          <button
            type="button"
            className="rf-dialog__close ds-resposta"
            aria-label={tx('close')}
            onClick={onClose}
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
        <RichText
          as="div"
          className="rf-terms"
          html={t('ambassador_terms', 'Ao participar, você concorda com os termos do programa.')}
        />
      </section>
    </div>
  );
}
